import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  Container,
  IconButton,
  Paper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Skeleton,
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/User/Header";
import { useSidebar } from "../../components/SidebarContext";
import { auth } from "../../config/firebaseConfig";
import { getApiUrl, getApiHeaders } from "../../config/api";

const humanizeCategory = (cat) => {
  if (!cat) return "System";
  const map = {
    gso: "General Services Office",
    system: "System",
    claim: "Claims",
    account: "Account",
    item_match: "Item Match",
  };
  return map[String(cat).toLowerCase()] || cat;
};

const Messages = () => {
  const [notifications, setNotifications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;
  const location = useLocation();
  const navigate = useNavigate();
  const highlightId = location.state?.highlightId;

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const user = auth.currentUser;
      if (!user) {
        setError("Please login to view notifications");
        setNotifications([]);
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch(getApiUrl("api/notifications?unread_only=false&limit=50"), {
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      if (!res.ok) throw new Error(`Failed to load notifications (${res.status})`);
      const data = await res.json();
      const items = (data.notifications || []).map(n => ({
        id: n.id,
        title: n.title,
        message: n.message,
        category: humanizeCategory(n.category),
        is_read: n.is_read,
        type: n.type,
        checked: false,
      }));
      setNotifications(items);
      setSelectAll(false);
    } catch (e) {
      setError(e.message || "Failed to load notifications");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Open dialog if navigated from overlay with highlightId
  useEffect(() => {
    if (highlightId && notifications.length > 0) {
      const notification = notifications.find(n => n.id === highlightId);
      if (notification) {
        setSelectedNotification(notification);
        setOpenDialog(true);
        // Clear the state after opening
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [highlightId, notifications, navigate, location.pathname]);

  const handleCheckboxChange = (id) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, checked: !notif.checked } : notif
      )
    );
  };

  const handleSelectAll = () => {
    const newCheckedState = !selectAll;
    setSelectAll(newCheckedState);
    setNotifications((prev) =>
      prev.map((notif) => ({ ...notif, checked: newCheckedState }))
    );
  };

  const handleDelete = () => {
    const toDelete = notifications.filter((n) => n.checked && n.type !== "broadcast");
    if (toDelete.length === 0) {
      // Nothing to delete or only broadcast selected
      setNotifications((prev) => prev.filter((n) => !(n.checked && n.type === "broadcast")));
      setSelectAll(false);
      return;
    }
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    const toDelete = notifications.filter((n) => n.checked && n.type !== "broadcast");
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        await Promise.all(
          toDelete.map((n) =>
            fetch(getApiUrl(`api/notifications/${n.id}`), {
              method: "DELETE",
              headers: getApiHeaders({ Authorization: `Bearer ${token}` }),
            })
          )
        );
      } catch (e) {
        // ignore delete errors for now
      } finally {
        setOpenDeleteDialog(false);
        fetchNotifications();
      }
    })();
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchNotifications();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setOpenDialog(true);
    
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(getApiUrl(`api/notifications/${notificationId}/read`), {
        method: "POST",
        headers: getApiHeaders({ Authorization: `Bearer ${token}` }),
      });
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedNotification(null);
  };

  return (
    <Box
      sx={{
        transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        bgcolor: "#FBFFE9",
        minHeight: "100vh",
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflowX: "hidden",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header title="Messages" />

      <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 3 }, pb: 4 }}>

        <Paper
          elevation={4}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            bgcolor: "#E1ECC8",
            borderRadius: "20px",
            position: "relative",
            minHeight: "600px",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" spacing={{ xs: 1, sm: 2 }} sx={{ mb: 3, alignItems: "center", pl: 1 }}>
            <Checkbox
              sx={{
                width: 34,
                height: 34,
                padding: 0,
                color: "#526D50", // Default color
                "&.Mui-checked": {
                  color: "#526D50", // Dark green when checked
                },
              }}
              checked={selectAll}
              onChange={handleSelectAll}
            />
            <IconButton color="primary" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon 
                sx={{ 
                  width: 27, 
                  height: 27, 
                  color: "#526D50",
                  animation: isRefreshing ? 'spin 0.6s linear' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }} 
              />
            </IconButton>
            <IconButton color="primary" onClick={handleDelete}>
              <DeleteIcon sx={{ width: 27, height: 27, color: "#526D50" }} />
            </IconButton>
          </Stack>

          <Stack spacing={2}>
            {error && (
              <Typography sx={{ color: "#c74f4f", pl: 1 }}>{error}</Typography>
            )}
            {loading && !error && (
              <>
                {[1, 2, 3, 4, 5].map((item) => (
                  <Stack key={item} direction="row" spacing={2} sx={{ px: 1 }}>
                    <Skeleton variant="rectangular" width={34} height={34} sx={{ borderRadius: '4px' }} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="30%" height={28} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="90%" height={20} />
                      <Skeleton variant="text" width="70%" height={20} />
                    </Box>
                  </Stack>
                ))}
              </>
            )}
            {!loading && notifications.map((notification) => (
              <Stack
                key={notification.id}
                spacing={1}
                sx={{ 
                  padding: "10px 0",
                  cursor: 'pointer',
                  borderRadius: '12px',
                  px: 1,
                  backgroundColor: notification.is_read ? 'transparent' : 'rgba(160, 196, 157, 0.15)',
                  transition: 'background-color 0.3s',
                  '&:hover': {
                    backgroundColor: 'rgba(160, 196, 157, 0.2)',
                  }
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ pl: 0 }}>
                  <Checkbox
                    sx={{
                      width: 34,
                      height: 34,
                      padding: 0,
                      color: "#526D50", // Default color
                      "&.Mui-checked": {
                        color: "#526D50", // Dark green when checked
                      },
                    }}
                    checked={notification.checked}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCheckboxChange(notification.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h3"
                      color="#526D50"
                      sx={{ 
                        fontSize: { xs: 16, sm: 18, md: 20 }, 
                        fontWeight: notification.is_read ? 400 : 700, 
                        mb: 0.5
                      }}
                    >
                      {notification.category}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="#526D50"
                      sx={{ 
                        fontSize: { xs: 14, sm: 15, md: 16 }, 
                        color: notification.is_read ? "#707070" : "#516d4f", 
                        fontWeight: notification.is_read ? 400 : 600,
                      }}
                    >
                      <strong style={{ fontWeight: notification.is_read ? 600 : 700 }}>{notification.title}</strong>
                      {": "}
                      {notification.message}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Paper>
      </Container>

      {/* Notification Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: '#E1ECC8',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          color: '#516d4f',
          fontWeight: 700,
          borderBottom: '2px solid #a0c49d',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
            {selectedNotification?.category}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: '#516d4f' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#516d4f', mb: 2, fontFamily: 'Poppins, sans-serif' }}>
            {selectedNotification?.title}
          </Typography>
          <Typography variant="body1" sx={{ color: '#707070', lineHeight: 1.6 }}>
            {selectedNotification?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            sx={{
              bgcolor: '#a0c49d',
              color: '#fbffe8',
              borderRadius: '12px',
              px: 3,
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#8ab287',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: '#E1ECC8',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#516d4f',
          fontWeight: 700,
          borderBottom: '2px solid #a0c49d',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
            Confirm Deletion
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ color: '#707070', lineHeight: 1.6 }}>
            Are you sure you want to delete {notifications.filter(n => n.checked && n.type !== "broadcast").length} notification(s)? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            sx={{
              borderColor: '#a0c49d',
              color: '#516d4f',
              borderRadius: '12px',
              px: 3,
              fontWeight: 600,
              '&:hover': {
                borderColor: '#8ab287',
                bgcolor: 'rgba(160, 196, 157, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              bgcolor: '#c74f4f',
              color: '#fff',
              borderRadius: '12px',
              px: 3,
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#a33f3f',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Messages;